import { VehicleValuationSettingKey } from "@/api-schema-enums";
import { useVehicleValuationSettingsGetQuery } from "@/common/api/vehicle-valuation-settings/use-vehicle-valuation-settings-get.query";
import { useVehicleValuationSettingsUpdateMutation } from "@/common/api/vehicle-valuation-settings/use-vehicle-valuation-settings-update.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InputTextAlign } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { Skeleton } from "@/components/ui/skeleton";
import { SaveIcon } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isValidityDays,
  MAX_VALIDITY_DAYS,
  parseValidityDays,
  toDraftNumber,
} from "../_helpers/fab-valuation-settings.helpers";

const VALIDITY_DAYS_INPUT_ID = "valuationValidityDays";

export function FabValuationValidity(): ReactElement {
  const { t } = useTranslation("fab");
  const settingsQuery = useVehicleValuationSettingsGetQuery(VehicleValuationSettingKey.ValidityDays);
  const settingsUpdateMutation = useVehicleValuationSettingsUpdateMutation();
  const [validityDaysDraft, setValidityDaysDraft] = useState<string | undefined>();

  const validityDaysValue = validityDaysDraft ?? toDraftNumber(settingsQuery.data?.value);
  const isDirty = validityDaysDraft !== undefined;
  const isValid = isValidityDays(validityDaysValue);
  const showError = validityDaysValue.trim().length > 0 && !isValid;
  const isHighlighted = isDirty && !settingsUpdateMutation.isPending;

  async function handleSave(): Promise<void> {
    if (!isValid) return;

    const settingLabel = t("ValuationValidityPeriod");
    await handleFormSubmission(
      () =>
        settingsUpdateMutation.mutateAsync({
          key: VehicleValuationSettingKey.ValidityDays,
          value: parseValidityDays(validityDaysValue),
        }),
      {
        success: t("SettingSaved", { setting: settingLabel }),
        error: t("SettingSaveError", { setting: settingLabel }),
      },
      () => {
        setValidityDaysDraft(undefined);
      },
    );
  }

  return (
    <section id="valuation-validity" className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("ValuationValidity")}</CardTitle>
        </CardHeader>

        <CardContent>
          {settingsQuery.isLoading && !settingsQuery.data ? (
            <div className="flex justify-between gap-4">
              <Skeleton className="h-5 w-48 self-center" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-40" />
                <Skeleton className="h-9 w-9 rounded-md" />
              </div>
            </div>
          ) : settingsQuery.isError && !settingsQuery.data ? (
            <p className="text-error text-sm">{t("ValuationValidityLoadError")}</p>
          ) : (
            <div className="flex flex-row justify-between">
              <div className="flex min-h-9 items-center">
                <label className="block text-sm leading-6 font-medium" htmlFor={VALIDITY_DAYS_INPUT_ID}>
                  {t("ValidityDays")}
                </label>
              </div>
              <div className="flex min-h-9 items-center space-x-2 md:justify-end">
                <div>
                  <LocalizedNumberInput
                    id={VALIDITY_DAYS_INPUT_ID}
                    min="1"
                    integerOnly
                    textAlign={InputTextAlign.Right}
                    value={validityDaysValue}
                    onValueChange={setValidityDaysDraft}
                    error={showError}
                    className="md:w-40"
                  />
                  {showError ? (
                    <p className="text-error text-xs">{t("EnterValidValidityDays", { max: MAX_VALIDITY_DAYS })}</p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant={isHighlighted ? ButtonVariant.Filled : ButtonVariant.Outlined}
                  colorVariant={isHighlighted ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
                  size={ButtonSize.Sm}
                  aria-label={t("SaveSetting")}
                  disabled={!isDirty || !isValid || settingsUpdateMutation.isPending}
                  onClick={() => void handleSave()}
                >
                  {settingsUpdateMutation.isPending ? (
                    <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                  ) : (
                    <SaveIcon />
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
